// Exportação XLSX da aba Financeiro > Cancelamentos.
//
// O arquivo gerado é EXATAMENTE o modelo que a importação consome
// (`COLUNAS_MODELO`), de propósito: o ciclo pretendido é exportar → completar
// no Excel → reimportar. Se as duas listas divergirem, o ciclo quebra — por
// isso a ordem das colunas vem de cancelamentosImport.ts, e não é redigitada
// aqui.
// `linhasExport` é pura e precisa rodar sob `node --test`; `./excelExport` e
// `./cancelamentosImport` não têm extensão `.ts` (é o padrão do Vite), então o
// Node não resolve um import estático deles. `type ColType`/`COLUNAS_MODELO`
// como type/const puro entram por import normal só onde não viram valor de
// runtime fora de teste; `buildSheet`/`downloadWorkbook`/`fileStamp` — que só
// as funções de I/O usam — entram via import dinâmico bem em cima de onde são
// chamadas. Mesma restrição documentada em src/lib/cancelamentos.ts.
import type { ColType } from './excelExport';
import { COLUNAS_MODELO } from './cancelamentosImport.ts';
import {
  MOTIVO_LABEL, STATUS_SOLICITACAO_LABEL, type SolicitacaoCancelamentoDetalhada,
} from './cancelamentos.ts';

const TIPOS: readonly ColType[] = [
  'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt',
  'kwh', 'kwh', 'kwh', 'txt', 'txt', 'txt', 'txt',
];

const LARGURAS = [16, 20, 34, 18, 20, 46, 24, 14, 30, 14, 18, 16, 34, 20, 22, 46];

const dataBR = (v: string | null | undefined): string => {
  if (!v) return '';
  const [ano, mes, dia] = v.slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : '';
};

const DISTRATO_TEXTO: Record<string, string> = { sim: 'Sim', nao: 'Não', aguardando: 'Aguardando assinatura' };

/**
 * Distrato exportado: o termo gerado pelo sistema tem precedência sobre o
 * campo informado na importação — é a mesma precedência que a tela aplica, e
 * inverter aqui faria a planilha exportada contradizer o que está na tela.
 */
const colunaDistrato = (s: SolicitacaoCancelamentoDetalhada): [string, string] => {
  if (s.distratoStatus === 'ativo') return ['Sim', s.distratoNumeroTermo || 'Termo gerado no sistema'];
  if (s.distratoStatus === 'cancelado') return ['Não', 'Termo do sistema cancelado'];
  if (s.distratoExterno) {
    const ref = [s.distratoExternoRef, dataBR(s.distratoExternoData)].filter(Boolean).join(' — ');
    return [DISTRATO_TEXTO[s.distratoExterno] ?? '', ref];
  }
  return ['Não', ''];
};

export function linhasExport(solicitacoes: SolicitacaoCancelamentoDetalhada[]): (string | number)[][] {
  return solicitacoes.map(s => {
    const [distrato, refTermo] = colunaDistrato(s);
    // Saldo: o calculado das faturas manda; o informado é fallback para o
    // histórico importado, cuja UC não tem fatura recente para calcular.
    const saldo = s.saldoAcumuladoKwh ?? s.saldoKwhInformado;
    return [
      s.uc_numero,
      s.cpf_cnpj_cliente || '',
      s.cliente_nome,
      dataBR(s.data_solicitacao),
      MOTIVO_LABEL[s.motivo_categoria],
      s.motivo_detalhe || '',
      s.vendedorNome || '',
      distrato,
      refTermo,
      saldo ?? '',
      // `|| fallback` estaria errado aqui: UC sem nenhuma fatura em aberto tem
      // valor 0 legítimo, e cairia no valor histórico da importação. Quem
      // decide é a QUANTIDADE de faturas, não o valor.
      s.faturasEmAbertoQtd > 0 ? s.faturasEmAbertoValor : (s.debitosAbertosValor ?? ''),
      s.multaValor ?? '',
      s.multaSituacao || '',
      dataBR(s.dataRetiradaRateio),
      STATUS_SOLICITACAO_LABEL[s.status],
      s.observacao || '',
    ];
  });
}

export async function exportarCancelamentos(solicitacoes: SolicitacaoCancelamentoDetalhada[]): Promise<void> {
  const { buildSheet, downloadWorkbook, fileStamp } = await import('./excelExport');
  const ws = buildSheet([...COLUNAS_MODELO], linhasExport(solicitacoes), TIPOS, {
    widths: LARGURAS,
    autofilter: true,
  });
  downloadWorkbook([{ name: 'Cancelamentos', ws }], `cancelamentos_${fileStamp()}.xlsx`);
}

/**
 * Planilha só com as linhas que a importação NÃO conseguiu gravar (UC não
 * encontrada no cadastro). Sem isso a equipe teria que reconferir a planilha
 * original inteira para achar 23 linhas — e provavelmente não faria.
 */
export async function exportarPendencias(
  pendencias: Array<{ aba: string; linha: number; ucBruta: string; cliente: string; motivo: string }>,
): Promise<void> {
  const { buildSheet, downloadWorkbook, fileStamp } = await import('./excelExport');
  const headers = ['Aba de origem', 'Linha', 'UC informada', 'Cliente', 'Por que não entrou'];
  const ws = buildSheet(
    headers,
    pendencias.map(p => [p.aba, p.linha, p.ucBruta, p.cliente, p.motivo]),
    ['txt', 'num', 'txt', 'txt', 'txt'],
    { widths: [22, 8, 20, 34, 52], autofilter: true },
  );
  downloadWorkbook([{ name: 'Pendências', ws }], `cancelamentos_pendencias_${fileStamp()}.xlsx`);
}
